import { useState, useEffect, useCallback } from 'react';
import {
  useParams,
  useNavigate,
  generatePath,
  useLocation,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonSecondary,
  PermissionEnum,
  Popup,
  Tabs,
  SidePanelLoader,
  ResponseError,
  responseErrorsResolver,
  ChangeLogs,
  PermissionDenied,
  getResponseErrors,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { RateRequest, RateRequestFormValues } from './types';
import RateRequestForm from '../../components/rate-request/form';
import {
  useLazyGetRateRequestDefaultQuery,
  useUpdateRateRequestsPairMutation,
  useCreateRateRequestPairMutation,
  useLazyGetRateRequestChangeLogsQuery,
  useChangeRateRequestStatusMutation,
} from './store/services';
import { clearDefaultRateRequestDetailsState } from './store/slices/sidepanel';

import ROUTES from '../../routes';

import cn from './rate-request.module.css';

const RateRequestGeneral = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'log'>('details');
  const [globalError, setGlobalError] = useState('');

  const dispatch = useAppDispatch();

  const {
    rateRequest: {
      sidepanel: { defaultRateRequest, changeLogs, changeLogsPagination },
    },
    exchangeDictionaries: { activePermissions, currencies },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);

  const [getDefaultRateRequest, { isLoading, isFetching }] =
    useLazyGetRateRequestDefaultQuery();
  const [update, { isLoading: requestInProgress }] =
    useUpdateRateRequestsPairMutation();
  const [create, { isLoading: createInProgress }] =
    useCreateRateRequestPairMutation();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetRateRequestChangeLogsQuery();
  const [changeStatus, { isLoading: changeStatusInProgress, isError }] =
    useChangeRateRequestStatusMutation();

  const { currency } = useParams();

  const {
    control,
    reset,
    formState: { errors, isDirty },
    handleSubmit,
    setError,
    watch,
    setValue,
  } = useForm<RateRequestFormValues>();

  const fields = watch();

  const status = watch('active');

  const navigate = useNavigate();
  const { search } = useLocation();

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=EXCHANGE_CUSTOM_RATE_RULE&entityId=${defaultRateRequest?.id}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [defaultRateRequest],
  );

  const onStatusChange = (active: boolean) => {
    setValue('active', active);

    changeStatus({
      id: defaultRateRequest?.id as string,
      active,
    });
  };

  const onSubmit = handleSubmit(async (values) => {
    let response;

    const body: RateRequestFormValues = {
      maxDeviationPercent: Number(values.maxDeviationPercent),
    };

    if (defaultRateRequest && defaultRateRequest.id) {
      response = (await update({
        id: defaultRateRequest?.id,
        body,
      })) as {
        error: ResponseError | { data: { message: string } };
        data?: { data: RateRequest };
      };
    } else {
      body.active = values.active;

      response = (await create(body)) as {
        error: ResponseError | { data: { message: string } };
        data?: { data: RateRequest };
      };
    }

    if (response) {
      if (response?.error) {
        responseErrorsResolver({
          errors: response.error as ResponseError,
          setError,
          setGlobalError,
        });
      } else {
        // eslint-disable-next-line no-lonely-if
        if (!defaultRateRequest) {
          const defaultRateResponse = (await getDefaultRateRequest()) as {
            error?: ResponseError;
          };

          if (defaultRateResponse?.error) {
            getResponseErrors({
              error: defaultRateResponse.error,
              setErrorList: setGlobalError,
            });
          }
        } else {
          reset({
            ...fields,
            ...body,
          });
        }
      }
    }
  });

  const getDefaultRateRequestAction = useCallback(async () => {
    const response = (await getDefaultRateRequest()) as {
      error?: ResponseError;
    };

    if (response?.error) {
      getResponseErrors({
        error: response.error,
        setErrorList: setGlobalError,
      });
    }
  }, []);

  const handleClose = () => {
    setPanelIsOpen(false);
    setDirtyPopupVisible(false);
    reset({});
    dispatch(clearDefaultRateRequestDetailsState());

    const url = generatePath(ROUTES.RATE_REQUEST_CURRENCY, {
      currency: currency as string,
    });

    setTimeout(() => {
      navigate(`${url}${search || ''}`);
    }, 100);
  };

  useEffect(() => {
    setPanelIsOpen(true);
    getDefaultRateRequestAction();
  }, []);

  useEffect(() => {
    if (isError) {
      setValue('active', !status);
    }
  }, [isError, status]);

  useEffect(() => {
    if (defaultRateRequest) {
      reset({
        maxDeviationPercent: defaultRateRequest.maxDeviationPercent,
        active: defaultRateRequest?.active,
      });
    }
  }, [defaultRateRequest]);

  useEffect(() => {
    if (
      activeTab === 'log' &&
      !changeLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [activeTab]);

  let content;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <>
        <div className={cn.settingsTabsWrapper}>
          {defaultRateRequest && (
            <Tabs
              tabs={[
                {
                  name: 'Details',
                  value: 'details',
                },
                {
                  name: 'Change log',
                  value: 'log',
                },
              ]}
              activeTab={activeTab}
              onClick={(tab) => setActiveTab(tab as 'details' | 'log')}
            />
          )}
        </div>
        {activeTab === 'details' && (
          <RateRequestForm
            control={control}
            errors={errors}
            currencies={currencies}
            isEditable={Boolean(
              activePermissions?.includes(
                PermissionEnum.SETTINGS_CUSTOM_RATE_EXCHANGE_UPDATE_ACTION,
              ),
            )}
            onStatusChange={onStatusChange}
            rateId={defaultRateRequest?.id}
            changeStatusInProgress={changeStatusInProgress}
            general
          />
        )}
        {activeTab === 'log' && (
          <>
            {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <ChangeLogs
                entityType="EXCHANGE_CUSTOM_RATE_RULE"
                entityId={defaultRateRequest?.id as string}
                changeLogsIsLoading={changeLogsIsLoading}
                changeLogs={changeLogs}
                activePermission={activePermissions}
                users={users}
                pagination={changeLogsPagination}
                changeLogsIsFetching={changeLogsIsFetching}
                getChangeLogs={getChangeLogsAction}
              />
            )}
            {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <PermissionDenied />
            )}
          </>
        )}
      </>
    );
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        errorSidepanel={globalError}
        onClose={() => {
          if (isDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        sidepanelExpandedKey="rate-request-panel"
        title="General Settings"
        className={cn.rateDetailsSidepanel}
        footer={
          <>
            {activePermissions?.includes(
              PermissionEnum.SETTINGS_CUSTOM_RATE_EXCHANGE_UPDATE_ACTION,
            ) && (
              <ButtonPrimary
                text={
                  requestInProgress || createInProgress ? 'Loading...' : 'Save'
                }
                onClick={onSubmit}
                disabled={!isDirty || requestInProgress || createInProgress}
                bold
              />
            )}

            <ButtonSecondary
              text="Close"
              onClick={() => {
                if (isDirty) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              className="button-white"
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default RateRequestGeneral;
